// src/components/Admin/GachaAndTitleManager.jsx
import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Paper,
    Button,
    Stack,
    TextField,
    Typography,
    Switch,
    MenuItem,
    useTheme,
    useMediaQuery,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
} from '@mui/material';

import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import RedeemIcon from '@mui/icons-material/Redeem';
import CasinoIcon from '@mui/icons-material/Casino';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';

import api from '../../api/axios';
import ProbabilityForm from './TitleProbabilityManager/ProbabilityForm';
import ProbabilityTable from './TitleProbabilityManager/ProbabilityTable';
import TitleTable from './TitleProbabilityManager/TitleTable';

const GachaAndTitleManager = () => {
    /* ─────────── UI 상태 ─────────── */
    const [tab, setTab] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600 px

    /* ─────────── 데이터 상태 ─────────── */
    const [configs, setConfigs] = useState([]); // 뽑기/럭키박스 config
    const [tiers, setTiers] = useState([]); // 등급 확률 + 가격
    const [titles, setTitles] = useState([]); // 칭호 목록

    /* 칭호 추가/검색 필터 */
    const [newTitleName, setNewTitleName] = useState('');
    const [newTitleTier, setNewTitleTier] = useState('');
    const [search, setSearch] = useState('');
    const [tierFilter, setTierFilter] = useState('all');

    const [loading, setLoading] = useState(false);

    /* ─────────── 초기 로드 ─────────── */
    useEffect(() => {
        fetchConfigs();
        fetchTiers();
        fetchTitles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ─────────── API 호출 ─────────── */
    const fetchConfigs = async () => {
        try {
            const res = await api.get('/api/gacha/config/');
            setConfigs(
                (res.data.configs || []).map((c) => ({
                    ...c,
                    price: Number.isFinite(c.price) ? c.price : 0,
                    duplicate_refund: Number.isFinite(c.duplicate_refund) ? c.duplicate_refund : 0,
                    enabled: typeof c.enabled === 'boolean' ? c.enabled : true,
                }))
            );
        } catch {
            alert('뽑기 설정을 불러오는 데 실패했습니다.');
        }
    };

    const fetchTiers = async () => {
        try {
            const res = await api.get('/api/rewards/tier-probability/');
            const data = (res.data.tiers || []).map((t) => ({
                ...t,
                price: typeof t.price === 'number' ? t.price : 0,
                duplicate_refund: Number.isFinite(t.duplicate_refund) ? t.duplicate_refund : 0,
                enabled: typeof t.enabled === 'boolean' ? t.enabled : true,
            }));
            setTiers(data);
        } catch {
            alert('등급 데이터를 불러오지 못했습니다.');
        }
    };

    const fetchTitles = () => {
        api
            .get('/api/rewards/titles/list/')
            .then((res) => setTitles(res.data.titles || []))
            .catch(() => alert('칭호 데이터를 불러오지 못했습니다.'));
    };

    /* ─────────── Config 수정 & 저장 ─────────── */
    const handleGachaChange = (index, key, value) => {
        setConfigs((prev) => prev.map((cfg, i) => (i === index ? { ...cfg, [key]: value } : cfg)));
    };

    const handleGachaSave = async () => {
        setLoading(true);
        try {
            await api.put('/api/gacha/config/update/', { configs });
            alert('저장 완료!');
            await fetchConfigs();
        } catch {
            alert('저장 실패');
        } finally {
            setLoading(false);
        }
    };

    /* ─────────── Tier(등급) 관련 ─────────── */
    const handlePriceChange = (index, value) => {
        const newList = [...tiers];
        const parsed = parseInt(value, 10);
        newList[index].price = Number.isNaN(parsed) ? 0 : parsed;
        setTiers(newList);
    };

    const handleRefundChange = (index, value) => {
        const newList = [...tiers];
        newList[index].duplicate_refund = parseInt(value, 10) || 0;
        setTiers(newList);
    };

    const handleToggle = (index, checked) => {
        const newList = [...tiers];
        newList[index].enabled = checked;
        setTiers(newList);
    };

    const handleAutoFill = () => {
        const total = tiers.reduce((sum, t) => sum + parseFloat(t.probability || 0), 0);
        const diff = 100 - total;
        if (tiers.length === 0 || Math.abs(diff) < 0.01) return;
        const newTiers = [...tiers];
        const last = newTiers.length - 1;
        newTiers[last].probability = (
            parseFloat(newTiers[last].probability || 0) + diff
        ).toFixed(2);
        setTiers(newTiers);
    };

    const handleTierSave = () => {
        const total = tiers.reduce((s, t) => s + parseFloat(t.probability || 0), 0);
        if (Math.abs(total - 100) > 0.01) {
            alert(`총합이 100%가 아닙니다 (${total.toFixed(2)}%)`);
            return;
        }
        const payload = tiers.map((t) => ({
            id: t.id,
            tier: (t.tier || '').trim(),
            probability: parseFloat(t.probability || 0),
            color: t.color || '#95a5a6',
            price: Number.isFinite(t.price) ? t.price : 0,
            duplicate_refund: Number.isFinite(t.duplicate_refund) ? t.duplicate_refund : 0,
            enabled: !!t.enabled,
        }));
        api
            .put('/api/rewards/tier-probability/', { tiers: payload })
            .then(() => alert('저장 완료!'))
            .catch(() => alert('저장 실패'));
    };

    const handleDeleteTier = (id) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        api
            .delete(`/api/rewards/tier-probability/${id}/`)
            .then(() => {
                alert('삭제되었습니다!');
                fetchTiers();
            })
            .catch(() => alert('삭제 실패'));
    };

    const updateTier = (index, updated) => {
        const newList = [...tiers];
        newList[index] = { ...tiers[index], ...updated };
        setTiers(newList);
    };

    /* ─────────── Title(칭호) 관련 ─────────── */
    const handleAddTitle = () => {
        if (!newTitleName || !newTitleTier) {
            alert('모든 항목을 입력해주세요.');
            return;
        }
        if (titles.some((t) => t.name === newTitleName)) {
            alert('중복된 이름입니다.');
            return;
        }
        api
            .post('/api/rewards/title-admin/', { name: newTitleName, tier: newTitleTier })
            .then(() => {
                alert('칭호 추가됨!');
                fetchTitles();
                setNewTitleName('');
                setNewTitleTier('');
            })
            .catch(() => alert('칭호 추가 실패'));
    };

    const handleDeleteTitle = (id) => {
        if (!window.confirm('정말 삭제?')) return;
        api
            .delete(`/api/rewards/title-admin/${id}/`)
            .then(() => fetchTitles())
            .catch(() => alert('삭제 실패'));
    };

    const updateTitle = (index, updated) => {
        api
            .put(`/api/rewards/title-admin/${updated.id}/update/`, updated)
            .then((res) => {
                if (res.data.success) {
                    const newList = [...titles];
                    newList[index] = updated;
                    setTitles(newList);
                    alert('칭호가 수정되었습니다!');
                } else {
                    alert(res.data.message || '수정에 실패했습니다.');
                }
            })
            .catch(() => alert('서버 오류로 수정에 실패했습니다.'));
    };

    /* ─────────── 필터링된 칭호 목록 ─────────── */
    const filteredTitles = useMemo(() => {
        return titles.filter(
            (t) =>
                (t.name || '').toLowerCase().includes(search.toLowerCase()) &&
                (tierFilter === 'all' || t.tier === tierFilter)
        );
    }, [titles, search, tierFilter]);

    /* ─────────── 모바일에서 입력 폭/버튼 폭 공통 ─────────── */
    const mobileInputSx = { width: isMobile ? 96 : 'auto' };

    /* ─────────── 렌더 ─────────── */
    return (
        <Box
            sx={{
                p: isMobile ? 2 : 4,
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden', // ✅ 페이지 전체 가로 삐져나감 방지(최후 안전장치)
            }}
        >
            {/* 헤더 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <MilitaryTechIcon sx={{ fontSize: '2rem' }} />
                <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 'bold' }}>
                    보상 시스템 관리자
                </Typography>
            </Box>

            {/* 탭 */}
            <Paper
                elevation={2}
                sx={{
                    mb: 3,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: '#f5f5f5',
                    overflowX: 'auto', // ✅ Tabs만 가로 스크롤 허용
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: 44,
                        '& .MuiTab-root': {
                            minHeight: 44,
                            px: 1,
                            whiteSpace: 'nowrap',
                            minWidth: isMobile ? 120 : 160,
                        },
                        '& .MuiTab-iconWrapper': {
                            mr: 1,
                        },
                    }}
                >
                    <Tab icon={<SettingsSuggestIcon />} iconPosition="start" label="뽑기 가격 설정" />
                    <Tab icon={<RedeemIcon />} iconPosition="start" label="럭키박스 가격 설정" />
                    <Tab icon={<CasinoIcon />} iconPosition="start" label="등급 확률 관리" />
                    <Tab icon={<AddCircleOutlineIcon />} iconPosition="start" label="칭호 추가/수정" />
                </Tabs>
            </Paper>

            {/* ───────── Tab 0 : 뽑기 가격 ───────── */}
            {tab === 0 && (
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    {configs.map((cfg, i) => (
                        <Stack
                            key={cfg.type}
                            direction={isMobile ? 'column' : 'row'}
                            spacing={2}
                            sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center' }}
                        >
                            {/* 가격 */}
                            <TextField
                                type="number"
                                label="가격"
                                size="small"
                                value={cfg.price}
                                onChange={(e) => handleGachaChange(i, 'price', parseInt(e.target.value, 10))}
                                inputProps={{ min: 0 }}
                                fullWidth={isMobile}
                            />

                            {/* 중복 환급 Leaf */}
                            <TextField
                                type="number"
                                label="중복 환급 Leaf"
                                size="small"
                                value={cfg.duplicate_refund}
                                onChange={(e) =>
                                    handleGachaChange(i, 'duplicate_refund', parseInt(e.target.value, 10))
                                }
                                inputProps={{ min: 0 }}
                                fullWidth={isMobile}
                            />

                            {/* 활성화 토글 */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>활성화</Typography>
                                <Switch
                                    checked={cfg.enabled}
                                    onChange={(_, checked) => handleGachaChange(i, 'enabled', checked)}
                                />
                            </Stack>
                        </Stack>
                    ))}
                    <Button variant="contained" onClick={handleGachaSave} disabled={loading}>
                        저장
                    </Button>
                </Paper>
            )}

            {/* ───────── Tab 1 : 럭키박스 가격 설정 ───────── */}
            {tab === 1 && (
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    {isMobile ? (
                        /* ================= 모바일: 카드형 ================= */
                        <Stack spacing={2}>
                            {tiers.map((tier, idx) => (
                                <Paper
                                    key={`${tier.id ?? 'temp'}-${idx}`}
                                    variant="outlined"
                                    sx={{ p: 2, borderRadius: 2 }}
                                >
                                    <Stack spacing={1.5}>
                                        {/* 헤더: 등급 / 활성화 */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 800 }}>
                                                {tier.tier}
                                            </Typography>

                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    활성
                                                </Typography>
                                                <Switch
                                                    checked={tier.enabled ?? true}
                                                    onChange={(e) => handleToggle(idx, e.target.checked)}
                                                />
                                            </Stack>
                                        </Box>

                                        {/* 가격 / 중복 환급 */}
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="가격"
                                                size="small"
                                                value={tier.price ?? 0}
                                                onChange={(e) => handlePriceChange(idx, e.target.value)}
                                                inputProps={{ min: 0 }}
                                            />
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="중복 환급"
                                                size="small"
                                                value={tier.duplicate_refund ?? 0}
                                                onChange={(e) => handleRefundChange(idx, e.target.value)}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Stack>

                                        {/* 참고 정보 */}
                                        <Stack direction="row" spacing={2}>
                                            <Typography variant="caption" color="text.secondary">
                                                확률: {tier.probability ?? '-'}%
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                색상: {tier.color ?? '-'}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        /* ================= PC: 테이블 ================= */
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>등급</TableCell>
                                    <TableCell>확률 (%)</TableCell>
                                    <TableCell>색상</TableCell>
                                    <TableCell>가격</TableCell>
                                    <TableCell>중복 환급</TableCell>
                                    <TableCell>활성화</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tiers.map((tier, idx) => (
                                    <TableRow key={`${tier.id ?? 'temp'}-${idx}`}>
                                        <TableCell>
                                            <TextField value={tier.tier} size="small" disabled />
                                        </TableCell>
                                        <TableCell>
                                            <TextField value={tier.probability} size="small" disabled />
                                        </TableCell>
                                        <TableCell>
                                            <TextField value={tier.color} size="small" disabled />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={tier.price ?? 0}
                                                onChange={(e) => handlePriceChange(idx, e.target.value)}
                                                inputProps={{ min: 0 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={tier.duplicate_refund ?? 0}
                                                onChange={(e) => handleRefundChange(idx, e.target.value)}
                                                inputProps={{ min: 0 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={tier.enabled ?? true}
                                                onChange={(e) => handleToggle(idx, e.target.checked)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {/* 저장 버튼 */}
                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            onClick={handleTierSave}
                            disabled={loading}
                            fullWidth={isMobile}
                        >
                            저장
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* ───────── Tab 2 : 등급 확률 관리 ───────── */}
            {tab === 2 &&
                (() => {
                    const total = tiers.reduce((s, t) => s + parseFloat(t.probability || 0), 0);
                    const isTotalValid = Math.abs(total - 100) <= 0.01;

                    return (
                        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                            <ProbabilityForm onAdd={(t) => setTiers([...tiers, t])} tiers={tiers} />
                            <ProbabilityTable tiers={tiers} onUpdate={updateTier} onDelete={handleDeleteTier} />
                            <Box
                                sx={{
                                    mt: 2,
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: isMobile ? 'column' : 'row',
                                    alignItems: isMobile ? 'stretch' : 'center',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleTierSave}
                                    disabled={!isTotalValid}
                                    fullWidth={isMobile}
                                >
                                    💾 저장
                                </Button>
                                <Button variant="outlined" onClick={handleAutoFill} fullWidth={isMobile}>
                                    🔄 자동 맞추기
                                </Button>
                                <Typography sx={{ color: isTotalValid ? 'text.secondary' : 'error.main' }}>
                                    총합: {total.toFixed(2)}%
                                </Typography>
                            </Box>
                        </Box>
                    );
                })()}

            {/* ───────── Tab 3 : 칭호 관리 ───────── */}
            {tab === 3 && (
                <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                    {/* 칭호 추가 */}
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={2} mb={2}>
                        <TextField
                            label="칭호 이름"
                            value={newTitleName}
                            onChange={(e) => setNewTitleName(e.target.value)}
                            size="small"
                            fullWidth={isMobile}
                        />
                        <TextField
                            label="등급"
                            select
                            value={newTitleTier}
                            onChange={(e) => setNewTitleTier(e.target.value)}
                            size="small"
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            fullWidth={isMobile}
                        >
                            {tiers.map((t) => (
                                <MenuItem key={t.tier} value={t.tier}>
                                    {t.tier}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button variant="contained" onClick={handleAddTitle} fullWidth={isMobile}>
                            칭호 추가
                        </Button>
                    </Stack>

                    {/* 검색 / 필터 */}
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={2} mb={2}>
                        <TextField
                            label="이름 검색"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            fullWidth={isMobile}
                        />
                        <TextField
                            label="등급 필터"
                            select
                            value={tierFilter}
                            onChange={(e) => setTierFilter(e.target.value)}
                            size="small"
                            sx={{ minWidth: isMobile ? '100%' : 150 }}
                            fullWidth={isMobile}
                        >
                            <MenuItem value="all">전체 등급</MenuItem>
                            {tiers.map((t) => (
                                <MenuItem key={t.tier} value={t.tier}>
                                    {t.tier}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    {/* 칭호 테이블 */}
                    {/* ✅ TitleTable 내부가 가로로 삐져나가면 TitleTable에서도 TableContainer(overflowX:auto) 적용 추천 */}
                    <TitleTable
                        titles={filteredTitles}
                        onDelete={handleDeleteTitle}
                        onUpdate={updateTitle}
                        tierOptions={tiers.map((t) => t.tier)}
                    />
                </Box>
            )}
        </Box>
    );
};

export default GachaAndTitleManager;
